import React from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  AlertTriangle,
  BarChart3,
  Clock3,
  Link2,
  MoreHorizontal,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Trash2,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Integration, useDeleteIntegration, useRotateKey, useUpdateIntegration } from "@/lib/integrations-store";

interface IntegrationCardProps {
  integration: Integration;
}

const STATUS_META = {
  connected: {
    label: "Connected",
    dot: "bg-emerald-500",
    badge: "border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    alert: null,
  },
  warning: {
    label: "Warning",
    dot: "bg-amber-500",
    badge: "border-amber-500/20 bg-amber-500/10 text-amber-600 dark:text-amber-400",
    alert: "Kết nối cần được rà soát cấu hình.",
  },
  expired: {
    label: "Expired",
    dot: "bg-red-500",
    badge: "border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-400",
    alert: "Thông tin xác thực đã hết hạn. Cần kết nối lại.",
  },
} as const;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);

const formatCalls = (value: number) => new Intl.NumberFormat("en-US").format(value);

const buildProviderLogo = (provider: string) => `https://logo.clearbit.com/${provider.toLowerCase()}.com`;

export function IntegrationCard({ integration }: IntegrationCardProps) {
  const rotateKey = useRotateKey();
  const deleteIntegration = useDeleteIntegration();
  const updateIntegration = useUpdateIntegration();
  const statusMeta = STATUS_META[integration.status];

  const handleRefresh = () => {
    updateIntegration(integration.id, {
      metrics: {
        ...integration.metrics,
        lastUsed: "just now",
      },
    });
    toast.success(`Đã làm mới trạng thái của ${integration.name}.`);
  };

  const handleRotateKey = () => {
    rotateKey(integration.id);
    toast.success(`Đã tạo khóa mới cho ${integration.name}.`);
  };

  const handleDelete = () => {
    deleteIntegration(integration.id);
    toast.error(`Đã ngắt kết nối ${integration.name}.`);
  };

  const handleReconnect = () => {
    rotateKey(integration.id);
    updateIntegration(integration.id, {
      status: "connected",
      metrics: {
        ...integration.metrics,
        lastUsed: "just now",
        successRate: Math.max(integration.metrics.successRate, 99),
      },
    });
    toast.success(`${integration.name} đã được kết nối lại.`);
  };

  const handleReview = () => {
    updateIntegration(integration.id, {
      status: "connected",
      metrics: {
        ...integration.metrics,
        successRate: Math.max(integration.metrics.successRate, 98),
      },
    });
    toast.success(`Đã xác nhận lại cấu hình của ${integration.name}.`);
  };

  const handleViewLogs = () => {
    toast.info(`Nhật ký gần nhất của ${integration.name}: trạng thái ${statusMeta.label.toLowerCase()}, dùng lần cuối ${integration.metrics.lastUsed}.`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      className="h-full"
    >
      <Card className="group flex h-full min-h-[248px] flex-col overflow-hidden rounded-[1.1rem] border border-white/35 bg-card/80 shadow-soft transition-all duration-200 hover:scale-[1.01] hover:border-primary/30 hover:shadow-glow focus-within:ring-2 focus-within:ring-ring">
        <div className="flex items-start justify-between border-b border-border/70 bg-foreground/[0.02] px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-border/70 bg-background/70 shadow-sm">
              <img
                src={buildProviderLogo(integration.provider)}
                alt={integration.provider}
                className="h-7 w-7 object-contain"
                loading="lazy"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                  const fallback = event.currentTarget.nextElementSibling as HTMLElement | null;
                  if (fallback) fallback.style.display = "flex";
                }}
              />
              <div className="hidden h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Link2 className="h-4 w-4" />
              </div>
            </div>

            <div className="min-w-0">
              <h3 className="truncate text-[1.02rem] font-semibold leading-tight">{integration.name}</h3>
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {integration.provider} • {integration.type}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${statusMeta.badge}`}>
              <span className={`h-2 w-2 rounded-full ${statusMeta.dot}`} />
              {statusMeta.label}
            </Badge>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" aria-label={`Tác vụ cho ${integration.name}`}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={handleRefresh} className="gap-2">
                  <RefreshCw className="h-4 w-4" /> Làm mới trạng thái
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleViewLogs} className="gap-2">
                  <BarChart3 className="h-4 w-4" /> Xem nhật ký gần nhất
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleRotateKey} className="gap-2">
                  <RotateCcw className="h-4 w-4" /> Tạo API key mới
                </DropdownMenuItem>
                {integration.status !== "connected" ? (
                  <DropdownMenuItem onClick={integration.status === "expired" ? handleReconnect : handleReview} className="gap-2">
                    <AlertTriangle className="h-4 w-4" /> {integration.status === "expired" ? "Kết nối lại" : "Xác nhận cấu hình"}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem onClick={handleDelete} className="gap-2 text-destructive focus:text-destructive">
                  <Trash2 className="h-4 w-4" /> Ngắt kết nối
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex flex-1 flex-col px-5 py-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-4">
            <MetricItem label="Calls" value={formatCalls(integration.metrics.calls)} hint="Số lượt gọi trong chu kỳ hiện tại" />
            <MetricItem label="Cost" value={formatCurrency(integration.metrics.cost)} valueClassName="text-indigo-600 dark:text-indigo-400" hint="Chi phí ước tính theo tháng" />
            <MetricItem
              label="Success"
              value={`${integration.metrics.successRate.toFixed(1)}%`}
              valueClassName={integration.metrics.successRate >= 98 ? "text-primary" : "text-amber-600 dark:text-amber-400"}
              icon={<ShieldCheck className="h-3.5 w-3.5 text-primary" />}
              hint="Tỷ lệ gọi thành công"
            />
            <MetricItem
              label="Last used"
              value={integration.metrics.lastUsed}
              valueClassName="text-foreground/80"
              icon={<Clock3 className="h-3.5 w-3.5 text-muted-foreground" />}
              hint="Lần dùng gần nhất"
            />
          </div>

          {statusMeta.alert ? (
            <div className="mt-4 flex items-start justify-between gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/10 px-3 py-2.5">
              <div className="flex items-start gap-2">
                <AlertTriangle className={`mt-0.5 h-4 w-4 ${integration.status === "expired" ? "text-red-500" : "text-amber-500"}`} />
                <p className="text-xs font-medium text-foreground">{statusMeta.alert}</p>
              </div>
              <Button
                type="button"
                size="sm"
                variant={integration.status === "expired" ? "destructive" : "outline"}
                className="h-8 rounded-full px-3 text-xs"
                onClick={integration.status === "expired" ? handleReconnect : handleReview}
              >
                {integration.status === "expired" ? "Reconnect" : "Review"}
              </Button>
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-primary/15 bg-primary/5 px-3 py-2.5">
              <p className="text-xs text-muted-foreground">Kết nối đang ổn định và không có cảnh báo cần xử lý ngay.</p>
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
}

function MetricItem({
  label,
  value,
  hint,
  icon,
  valueClassName,
}: {
  label: string;
  value: string;
  hint: string;
  icon?: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-2xl border border-border/70 bg-background/55 px-3 py-3">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground" title={hint}>
        {icon}
        <span>{label}</span>
      </div>
      <p className={`truncate text-sm font-semibold ${valueClassName ?? ""}`}>{value}</p>
    </div>
  );
}
